/**
 * 搜索后端注册表（T12a）。
 *
 * 与 `src/core/providers/registry.ts` 同构：
 * - 按 `SearchConfig.kind` 实例化适配器；
 * - 支持运行时注册 / 覆盖自定义适配器（`kind: 'custom'` 或覆盖内置 kind）；
 * - 对未注册 kind 给出明确错误，而不是静默降级。
 *
 * 注册表只持有「工厂函数」，不持有配置与密钥，因此同一个注册表可服务任意多个配置实例。
 */

import { createTavilyProvider, TAVILY_KIND } from './tavily';
import type {
  SearchConfig,
  SearchProvider,
  SearchProviderFactory,
  SearchDeps,
  ResolvedSearchConfig,
} from './types';

export const BUILTIN_SEARCH_KINDS: readonly SearchConfig['kind'][] = [
  'tavily',
  'brave',
  'searxng',
  'custom',
] as const;

/**
 * 注册表 API 接受的 kind 参数：任意字符串（支持自定义后端），同时保留内置 kind 的字面量补全。
 *
 * 不能写成 `SearchProviderKind | string`——该联合会被 `string` 完全吸收，属于冗余类型分量。
 * 用 `SearchProviderKind | (string & {})` 让联合保持可辨识。
 */
export type SearchKindInput = SearchConfig['kind'] | (string & {});

/** 未注册 kind 时抛出；上层可捕获后提示用户配置。 */
export class SearchNotRegisteredError extends Error {
  readonly kind: string;

  constructor(kind: string) {
    super(`没有为 search kind "${kind}" 注册搜索适配器`);
    this.name = 'SearchNotRegisteredError';
    this.kind = kind;
  }
}

export class SearchRegistry {
  private readonly factories = new Map<string, SearchProviderFactory>();

  constructor(options?: { withDefaults?: boolean }) {
    if (options?.withDefaults !== false) {
      this.registerDefaults();
    }
  }

  /**
   * 注册内置搜索后端。T12a 只实现 Tavily；
   * Brave / SearXNG 由后续任务注册（`custom` 预留给自建 SearXNG 等兼容服务）。
   */
  registerDefaults(): this {
    this.register(TAVILY_KIND, createTavilyProvider);
    return this;
  }

  /** 注册（或覆盖）某个 kind 的适配器工厂。 */
  register(kind: SearchKindInput, factory: SearchProviderFactory): this {
    if (typeof factory !== 'function') {
      throw new TypeError(`search 工厂必须是函数，收到 ${typeof factory}`);
    }
    this.factories.set(kind, factory);
    return this;
  }

  /** 注销某个 kind。 */
  unregister(kind: SearchKindInput): boolean {
    return this.factories.delete(kind);
  }

  has(kind: SearchKindInput): boolean {
    return this.factories.has(kind);
  }

  /** 已注册的 kind 列表（便于设置页展示与排错）。 */
  kinds(): string[] {
    return [...this.factories.keys()];
  }

  /** 按配置实例化适配器。 */
  create(config: ResolvedSearchConfig, deps?: SearchDeps): SearchProvider {
    const factory = this.factories.get(config.kind);
    if (!factory) {
      throw new SearchNotRegisteredError(config.kind);
    }
    return factory(config, deps);
  }

  /** 批量实例化，跳过 `enabled === false` 的配置。 */
  createEnabled(configs: readonly ResolvedSearchConfig[], deps?: SearchDeps): SearchProvider[] {
    return configs.filter((config) => config.enabled).map((config) => this.create(config, deps));
  }
}

/** 进程内默认注册表（内置 kind 已注册）。 */
export const defaultSearchRegistry = new SearchRegistry();

/** 便捷函数：用默认注册表实例化。 */
export function createSearchProvider(
  config: ResolvedSearchConfig,
  deps?: SearchDeps,
): SearchProvider {
  return defaultSearchRegistry.create(config, deps);
}

/**
 * 归一化外部配置：补齐必填字段并保证 `enabled` 为布尔值。
 * 用于从设置页 / 存储层读取到的宽松对象。
 */
export function normalizeSearchConfig(
  config: SearchConfig & Partial<Pick<ResolvedSearchConfig, 'apiKey' | 'headers'>>,
): ResolvedSearchConfig {
  return {
    ...config,
    name: config.name || config.kind,
    baseUrl: config.baseUrl,
    apiKeyRef: config.apiKeyRef,
    enabled: config.enabled ?? true,
  };
}
